import { useEffect, useRef, useState } from "react";
import { loadEvidencePhoto } from "./api";

/** Photo bytes and object URLs live only within the current viewer and scope. */
export function useEvidencePhotos(scope: string) {
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const urls = useRef<Record<string, string>>({});
  const pending = useRef(new Map<string, AbortController>());
  const [snapshot, setSnapshot] = useState({
    scope,
    urls: {} as Record<string, string>,
  });
  useEffect(() => {
    setSnapshot({ scope, urls: {} });
    return () => {
      for (const request of pending.current.values()) request.abort();
      pending.current.clear();
      for (const url of Object.values(urls.current)) URL.revokeObjectURL(url);
      urls.current = {};
    };
  }, [scope]);
  function clear(id?: string) {
    const ids = id
      ? [id]
      : [...new Set([...Object.keys(urls.current), ...pending.current.keys()])];
    for (const key of ids) {
      pending.current.get(key)?.abort();
      pending.current.delete(key);
      if (urls.current[key]) URL.revokeObjectURL(urls.current[key]);
      delete urls.current[key];
    }
    setSnapshot({ scope: scopeRef.current, urls: { ...urls.current } });
  }
  async function preview(id: string) {
    clear();
    const expectedScope = scopeRef.current;
    const controller = new AbortController();
    pending.current.set(id, controller);
    try {
      const blob = await loadEvidencePhoto(id, controller.signal);
      if (controller.signal.aborted || expectedScope !== scopeRef.current)
        return;
      const url = URL.createObjectURL(blob);
      urls.current[id] = url;
      setSnapshot({ scope: expectedScope, urls: { ...urls.current } });
    } finally {
      if (pending.current.get(id) === controller) pending.current.delete(id);
    }
  }
  return {
    photos: snapshot.scope === scope ? snapshot.urls : {},
    preview,
    clear,
  };
}
